import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Select, Typography, Space, Row, Tag, Alert, Tabs, Progress, Skeleton } from 'antd';

const { Title, Text } = Typography;

// Declare module augmentation for styled-components theme
declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 24px;
  min-height: 100vh;
  background: ${(props) => getThemeValue(props, '#171923', '#f5f5f5')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;


export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const IconBox = styled.div<{ bg?: string; color?: string }>`
  padding: 12px;
  border-radius: 12px;
  background: ${({ bg }) => bg || 'rgba(49, 130, 206, 0.1)'};
  color: ${({ color }) => color || '#3182ce'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MainTitle = styled(Title)`
  margin: 0 !important;
  font-size: 28px !important;
  font-weight: 700 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')} !important;
  
  @media (max-width: 768px) {
    font-size: 24px !important;
  }
`;

export const Subtitle = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
`;

export const ControlsRow = styled(Space)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
    
    .ant-select {
      width: 100% !important;
    }
    
    .ant-btn {
      flex: 1;
    }
  }
`;

export const StyledSelect = styled(Select)`
  min-width: 160px;
  
  .ant-select-selector {
    border-radius: 8px !important;
    border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#d9d9d9')} !important;
    background: ${(props) => getThemeValue(props, '#1a202c', '#fff')} !important;
  }
`;

export const StyledButton = styled(Button)`
  border-radius: 8px;
  height: 40px;
  background-color: #2D6CCA !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  color: #fff !important;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  
  &:hover {
    background-color: ${(props) => getThemeValue(props, '#1a5499', '#1a5499')} !important;
  }
`;

export const TileValueSkeleton = styled(Skeleton.Input)`
  width: 100px;
  height: 32px;
  margin-bottom: 8px;
`;

export const ProgressPercentageSkeleton = styled(Skeleton.Input)`
  width: 30px;
  height: 14px;
`;

export const ProgressBarSkeleton = styled(Skeleton.Input)`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  margin-top: 8px;
`;

export const TilesGrid = styled(Row)`
  gap: 24px;
  
  .ant-col {
    margin-bottom: 24px;
  }
  
  @media (min-width: 768px) {
    .ant-col {
      flex: 0 0 calc(33.333333% - 16px);
      max-width: calc(33.333333% - 16px);
    }
  }
  
  @media (max-width: 767px) {
    .ant-col {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
`;

export const AnalyticsTileCard = styled(Card)<{ accentColor?: string }>`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  height: 100%;
  overflow: hidden;
  position: relative;
  
  /* Top border - always visible */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ accentColor }) => accentColor || '#3182ce'};
    z-index: 1;
    border-radius: 16px 16px 0 0;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => getThemeValue(props, '0 8px 24px rgba(0, 0, 0, 0.4)', '0 8px 24px rgba(0, 0, 0, 0.15)')};
    /* border-color: ${({ accentColor }) => accentColor || '#3182ce'}; */
  }
  
  .ant-card-body {
    padding: 24px;
  }
`;

export const TileIconBox = styled.div<{ bg?: string; color?: string }>`
  padding: 16px;
  border-radius: 12px;
  background: ${({ bg }) => bg || 'rgba(49, 130, 206, 0.1)'};
  color: ${({ color }) => color || '#3182ce'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
`;

export const TileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TileTitle = styled(Text)`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
`;

export const TileValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const TileSubtitle = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
  margin-top: 4px;
`;

export const TrendBadge = styled(Tag)`
  border-radius: 12px;
  padding: 4px 12px;
  font-weight: 500;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const ChartCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  margin-bottom: 24px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

export const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

export const ChartTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ChartTitle = styled(Title)`
  margin: 0 !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')} !important;
`;

export const ChartDescription = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
`;

export const ChartContainer = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
`;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
    border-radius: 12px;
    padding: 6px;
    margin-bottom: 0;
    margin-top: 0;
    border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
    box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  }
  
  .ant-tabs-nav::before {
    border-bottom: none !important;
  }
  
  .ant-tabs-tab {
    border-radius: 8px;
    margin: 0 2px;
    padding: 8px 16px;
    font-weight: 500;
    font-size: 14px;
    transition: all 0.3s ease;
    border: none;
    color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
    
    &:hover {
      color: ${(props) => getThemeValue(props, '#fff', '#1890ff')};
    }
    
    &.ant-tabs-tab-active {
      background: ${(props) => getThemeValue(props, '#2d3748', '#e6f4ff')};
      color: ${(props) => getThemeValue(props, '#fff', '#1890ff')};
      
      .ant-tabs-tab-btn {
        color: ${(props) => getThemeValue(props, '#fff', '#1890ff')};
        font-weight: 600;
      }
    }
  }
  
  .ant-tabs-ink-bar {
    display: none !important;
  }
  
  .ant-tabs-content-holder {
    padding: 0;
    margin-top: 16px;
  }
  
  .ant-tabs-content {
    margin-top: 0;
  }
`;

export const StyledAlert = styled(Alert)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  margin-bottom: 16px;
  padding: 12px !important;
  .ant-alert-icon {
    color: ${(props) => getThemeValue(props, '#AAAAAA', '#AAAAAA')} !important;
    font-size: 19px !important;
  }
`;

export const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${(props) => getThemeValue(props, '#171923', '#f5f5f5')};
`;

export const ProgressContainer = styled.div`
  margin-top: 16px;
`;

export const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
`;

export const TileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const TileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const TrendIconWrapper = styled.span`
  margin-right: 4px;
`;

export const StyledProgress = styled(Progress)`
  margin-top: 8px;
`;